'use client';

import * as React from 'react';
import { useRoomContext } from '@livekit/components-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useHostSettings } from '@/lib/HostSettingsContext';
import { useHostControl } from '@/lib/HostControlContext';
import { playToastAlertSound } from '@/lib/notificationSound';
import {
  parseRecordingEvent,
  publishRecordingEvent,
  recordingActionToWindowEvent,
  type RecordingAction,
} from '@/lib/recordingEvents';
import {
  fetchRecordingStatus,
  isConfirmedFileRecording,
  isPreparingVisualState,
  isRecordingStartingState,
  isRecordingVisualState,
  normalizeRecordingUiState,
  postCancelRecording,
  startRecordingFromRoom,
  windowEventForRecordingUiState,
  type RecordingUiState,
} from '@/lib/recording-status-client';
import {
  RECORDING_PREPARING_BUTTON_TITLE,
  RECORDING_PREPARING_TOAST,
  RECORDING_RETRY_BUTTON_LABEL,
  RECORDING_STARTING_BUTTON_TITLE,
  RECORDING_STARTING_TITLE,
  RECORDING_START_FAILED_BODY,
} from '@/lib/recording-ui-messages';
import {
  announceRecordingStartFailed,
  markRecordingAwaitingDismissedByUser,
} from '@/lib/recording-start-failed';
import toast from 'react-hot-toast';

type RecordButtonProps = {
  inline?: boolean;
};

export function RecordButton({ inline = false }: RecordButtonProps) {
  const room = useRoomContext();
  const { isHost } = useHostSettings();
  const { isCoHost } = useHostControl();
  const [processing, setProcessing] = React.useState(false);
  const [uiState, setUiState] = React.useState<RecordingUiState>('idle');
  const [leftPx, setLeftPx] = React.useState<number | null>(null);
  const [showStopDialog, setShowStopDialog] = React.useState(false);
  const pollRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const lastStateRef = React.useRef<RecordingUiState>('idle');

  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const recordingEndpoint = process.env.NEXT_PUBLIC_LK_RECORD_ENDPOINT || '/api/record';
  const canControlRecording = isHost || isCoHost;

  const dispatchWindowEvent = React.useCallback((state: RecordingUiState) => {
    window.dispatchEvent(new CustomEvent(windowEventForRecordingUiState(state)));
  }, []);

  const applyRemoteState = React.useCallback(
    (state: RecordingUiState) => {
      const normalized =
        state === 'stopping' ? 'idle' : state;
      lastStateRef.current = normalized;
      setUiState(normalized);
      dispatchWindowEvent(normalized);
    },
    [dispatchWindowEvent],
  );

  const stopPolling = React.useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const notifyRecordingAction = React.useCallback(
    (action: RecordingAction, actorName: string, opts?: { playSound?: boolean; queuePosition?: number }) => {
      if (action === 'failed') return;
      const messages: Record<string, string> = {
        preparing: RECORDING_PREPARING_TOAST,
        starting: RECORDING_STARTING_TITLE,
        started: `${actorName} iniciou a gravação.`,
        stopped: `${actorName} parou a gravação.`,
        failed: 'Não foi possível concluir a gravação.',
        cancelled: 'Preparo da gravação cancelado.',
      };
      const message = messages[action] ?? messages.started;

      if (opts?.playSound) playToastAlertSound();
      toast(message, { icon: action === 'stopped' ? '⏹️' : action === 'started' ? '⏺️' : '⏳' });
    },
    [],
  );

  const startPolling = React.useCallback(
    (roomName: string) => {
      stopPolling();
      pollRef.current = setInterval(async () => {
        try {
          const status = await fetchRecordingStatus(roomName);
          const state = normalizeRecordingUiState(status);
          const prev = lastStateRef.current;
          applyRemoteState(state);
          if (isConfirmedFileRecording(status) && prev !== 'active') {
            const actorName = room.localParticipant?.name || 'Alguém';
            notifyRecordingAction('started', actorName);
            publishRecordingEvent(room, 'started');
          }
          if (isConfirmedFileRecording(status) || state === 'idle' || state === 'failed') {
            stopPolling();
          }
        } catch (err) {
          console.warn('[RecordButton] poll status failed', err);
        }
      }, 2000);
    },
    [applyRemoteState, notifyRecordingAction, room, stopPolling],
  );

  React.useEffect(() => () => stopPolling(), [stopPolling]);

  React.useEffect(() => {
    if (!room?.name) return;
    fetchRecordingStatus(room.name)
      .then((s) => applyRemoteState(normalizeRecordingUiState(s)))
      .catch(() => applyRemoteState('idle'));
  }, [room?.name, applyRemoteState]);

  React.useEffect(() => {
    if (!room) return;

    const handleData = (
      payload: Uint8Array,
      participant?: { name?: string | null; identity?: string | null },
    ) => {
      const result = parseRecordingEvent(payload, participant?.name ?? null);
      if (!result) return;

      const isLocal =
        !!result.actorIdentity && result.actorIdentity === room.localParticipant?.identity;

      if (!isLocal) {
        notifyRecordingAction(result.action, result.actorName, {
          playSound: true,
          queuePosition: result.queuePosition,
        });
      }

      const mapped: RecordingUiState =
        result.action === 'started'
          ? 'active'
          : result.action === 'starting'
            ? 'starting'
            : result.action === 'preparing'
              ? 'preparing'
              : result.action === 'failed'
                ? 'failed'
                : 'idle';
      applyRemoteState(mapped);
      window.dispatchEvent(new CustomEvent(recordingActionToWindowEvent(result.action)));
    };

    room.on('dataReceived', handleData);
    return () => {
      room.off('dataReceived', handleData);
    };
  }, [applyRemoteState, notifyRecordingAction, room]);

  const displayRecording = isRecordingVisualState(uiState);
  const displayStarting = isRecordingStartingState(uiState);
  const displayPreparing = isPreparingVisualState(uiState);
  const displayFailed = uiState === 'failed';
  const isStartDisabled =
    !displayRecording && !displayStarting && !displayPreparing && !displayFailed && !canControlRecording;
  const tooltipLabel = displayRecording
    ? 'Parar a gravação'
    : displayStarting
      ? RECORDING_STARTING_BUTTON_TITLE
      : displayPreparing
        ? RECORDING_PREPARING_BUTTON_TITLE
        : displayFailed
          ? RECORDING_RETRY_BUTTON_LABEL
          : isStartDisabled
            ? 'Somente anfitriões ou co-anfitriões podem iniciar a gravação'
            : 'Iniciar gravação';

  React.useEffect(() => {
    if (inline) return;
    if (typeof window === 'undefined') return;
    const container = document.querySelector('.lk-room-container') as HTMLElement | null;
    const bar = document.querySelector('.lk-control-bar') as HTMLElement | null;
    if (!container || !bar) return;

    const update = () => {
      const cr = container.getBoundingClientRect();
      const br = bar.getBoundingClientRect();
      setLeftPx(br.left + br.width + 16 - cr.left);
    };

    update();
    const ro = new ResizeObserver(update);
    ro.observe(container);
    ro.observe(bar);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [inline]);

  const startRecording = async () => {
    if (!room?.name || !canControlRecording) return;

    try {
      setProcessing(true);
      const result = await startRecordingFromRoom(room);

      if (result.httpStatus === 202 || result.state === 'preparing') {
        setUiState('preparing');
        dispatchWindowEvent('preparing');
        publishRecordingEvent(room, 'preparing', { queuePosition: result.queuePosition });
        notifyRecordingAction('preparing', room.localParticipant.name || 'Alguém', {
          queuePosition: result.queuePosition,
        });
        startPolling(room.name);
        return;
      }

      if (result.httpStatus === 409 && result.isRecordingFile) {
        setUiState('active');
        dispatchWindowEvent('active');
        return;
      }

      if (result.httpStatus >= 400) {
        announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
        setUiState('idle');
        dispatchWindowEvent('idle');
        return;
      }

      setUiState(result.state === 'active' ? 'active' : 'starting');
      dispatchWindowEvent(result.state === 'active' ? 'active' : 'starting');
      startPolling(room.name);

      if (result.state === 'starting' || (result.state === 'active' && !result.egressId)) {
        const actorName = room.localParticipant.name || 'Alguém';
        notifyRecordingAction('starting', actorName);
        publishRecordingEvent(room, 'starting');
      } else if (result.state === 'active' && result.egressId) {
        const actorName = room.localParticipant.name || 'Alguém';
        notifyRecordingAction('started', actorName);
        publishRecordingEvent(room, 'started');
      }
    } catch (err) {
      console.error(err);
      announceRecordingStartFailed(RECORDING_START_FAILED_BODY);
      setUiState('idle');
      dispatchWindowEvent('idle');
    } finally {
      setProcessing(false);
    }
  };

  const cancelPreparing = async () => {
    if (!room?.name) return;
    markRecordingAwaitingDismissedByUser();
    await postCancelRecording(room.name);
    stopPolling();
    setUiState('idle');
    dispatchWindowEvent('idle');
    publishRecordingEvent(room, 'cancelled');
    notifyRecordingAction('cancelled', room.localParticipant?.name || 'Alguém');
  };

  const stopRecording = async () => {
    if (!room?.name || !canControlRecording) return;

    try {
      if (displayStarting) markRecordingAwaitingDismissedByUser();
      setProcessing(true);
      setUiState('stopping');
      dispatchWindowEvent('stopping');

      const url = `${recordingEndpoint}/stop?roomName=${encodeURIComponent(room.name)}`;
      const response = await fetch(url);
      if (!response.ok && response.status !== 404) {
        toast.error('Não foi possível parar a gravação.');
        setUiState('active');
        dispatchWindowEvent('active');
        return;
      }

      setUiState('idle');
      dispatchWindowEvent('idle');
      const actorName = room.localParticipant.name || 'Alguém';
      notifyRecordingAction('stopped', actorName);
      publishRecordingEvent(room, 'stopped');
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const toggle = async () => {
    if (!recordingEndpoint || !room) return;
    if (room.isE2EEEnabled) {
      toast.error('Gravação indisponível com criptografia ponta a ponta (E2EE) ativada.');
      return;
    }

    if (displayPreparing) {
      if (canControlRecording) await cancelPreparing();
      return;
    }

    if (displayRecording || displayStarting) {
      if (!canControlRecording) {
        toast.error('Somente anfitriões ou co-anfitriões podem parar a gravação.');
        return;
      }
      setShowStopDialog(true);
      return;
    }

    if (!canControlRecording) {
      toast.error('Somente anfitriões ou co-anfitriões podem iniciar uma gravação.');
      return;
    }

    await startRecording();
  };

  if (!recordingEndpoint) return null;

  const baseBg = 'var(--lk-bg2)';
  const baseFg = 'var(--lk-fg)';
  const awaitingFile = displayPreparing || displayStarting;
  const activeBg = awaitingFile ? 'rgba(245, 158, 11, 0.85)' : 'var(--lk-danger3)';

  const Btn = (
    <button
      className="lk-button"
      disabled={processing}
      onClick={toggle}
      aria-pressed={displayRecording || displayStarting || displayPreparing}
      aria-label={tooltipLabel}
      aria-disabled={isStartDisabled}
      data-tooltip={tooltipLabel}
      style={{
        width: 44,
        height: 44,
        padding: 0,
        borderRadius: 8,
        background: displayRecording || awaitingFile ? activeBg : baseBg,
        color: baseFg,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 0 0 1px var(--lk-border-color, rgba(255,255,255,0.08)) inset',
        opacity: processing || isStartDisabled ? 0.7 : 1,
        cursor: processing || isStartDisabled ? 'not-allowed' : 'pointer',
        transition: 'all 0.2s ease-in-out',
        animation: processing || awaitingFile ? 'blink 1s ease-in-out infinite' : 'none',
      }}
    >
      {displayRecording ? '⏹' : awaitingFile ? '⏳' : '⏺'}
    </button>
  );

  const dialog = (
    <Dialog open={showStopDialog} onOpenChange={setShowStopDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Parar gravação</DialogTitle>
          <DialogDescription>Tem certeza que deseja parar a gravação?</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setShowStopDialog(false)} disabled={processing}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => {
              setShowStopDialog(false);
              stopRecording();
            }}
            disabled={processing}
          >
            {processing ? 'Parando...' : 'Parar gravação'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (inline) {
    return (
      <>
        {Btn}
        {dialog}
      </>
    );
  }

  return (
    <>
      <div className="record-btn-overlay" ref={containerRef} style={{ left: leftPx ?? undefined }}>
        {Btn}
      </div>
      {dialog}
    </>
  );
}
